import { useMemo } from 'react';
import { VisibilityMap } from '../../types/VisibilityTypes';
import { TransientFunnel } from './funnelMapCanvasTypes';
import { PillsMap } from '../canvas-pills/canvasPillsReducer';
import { Dictionary } from 'lodash';
import { useCalculateThirdPartyNodes } from './calculate-nodes/useCalculateThirdPartyNodes';
import { useCalculateSfFunnelsNodes } from './calculate-nodes/useCalculateSfFunnelsNodes';
import { useCalculateMetadataElementStandaloneNodes } from './calculate-nodes/useCalculateMetadataElementStandaloneNodes';
import { useCalculateSectionNodes } from './calculate-nodes/useCalculateSectionNodes';
import { FunnelStatus } from '../../types/enums/FunnelStatus';
import { SfFunnelMoreButtonProps } from './types';
import { CrmOrg } from '../../types/CrmOrgs';
import { useCalculateStickyNotesNodes } from './calculate-nodes/useCalculateStickyNotesNodes';
import { useCalculateTextNodes } from './calculate-nodes/useCalculateTextNodes';

export const TRANSIENT_GROUP_ID = 'TRANSIENT_GROUP';

export const useFunnelMapElements = ({
  funnelMap,
  transientFunnel,
  renderZeroCountPills,
  visibilityMap,
  orgPillsMap,
  objectTypesByName,
  editingThirdPartyStepId,
  stagesWithSparkleIcon,
  mapForecastCategoryValueToLabel,
  crmOrgs,
  funnelStatusMap,
  sfFunnelMoreButton,
}: {
  funnelMap: FunnelMap;
  visibilityMap: VisibilityMap;
  transientFunnel?: TransientFunnel;
  renderZeroCountPills?: boolean;
  orgPillsMap?: { [crmOrgId: string]: PillsMap };
  objectTypesByName: Dictionary<ObjectTypeName>;
  editingThirdPartyStepId?: string;
  stagesWithSparkleIcon?: string[];
  mapForecastCategoryValueToLabel?: (value: string) => string;
  crmOrgs?: CrmOrg[];
  funnelStatusMap?: Dictionary<FunnelStatus>;
  sfFunnelMoreButton?: SfFunnelMoreButtonProps;
}) => {
  const sfFunnelNodes = useCalculateSfFunnelsNodes({
    funnelMap,
    visibilityMap,
    renderZeroCountPills,
    orgPillsMap,
    transientFunnel,
    objectTypesByName,
    stagesWithSparkleIcon,
    mapForecastCategoryValueToLabel,
    crmOrgs,
    funnelStatusMap,
    sfFunnelMoreButton,
  });
  const thirdPartyNodes = useCalculateThirdPartyNodes({
    funnelMap,
    transientFunnel,
    editingThirdPartyStepId,
    stagesWithSparkleIcon,
  });

  const metadataElementStandaloneNodes = useCalculateMetadataElementStandaloneNodes({
    funnelMap,
    crmOrgs,
    visibilityMap,
  });

  const sectionNodes = useCalculateSectionNodes({
    funnelMap,
  });

  const { stickyNotesNodes } = useCalculateStickyNotesNodes({
    stickyNotesMap: funnelMap.stickyNotes,
    transientFunnel,
  });

  const { textNodes } = useCalculateTextNodes({
    textsMap: funnelMap.texts,
    transientFunnel,
  });

  return useMemo(() => {
    const sweepNodes = [...sfFunnelNodes.nodes, ...thirdPartyNodes.nodes];
    const sweepSections = [...sectionNodes.nodes];
    const sweepEdges = [
      ...sfFunnelNodes.edges,
      ...thirdPartyNodes.edges,
      ...metadataElementStandaloneNodes.edges,
    ];
    const sweepGroups = [...sfFunnelNodes.groups, ...thirdPartyNodes.groups];

    const sweepStandaloneNodes = [...metadataElementStandaloneNodes.standaloneNodes];

    const sweepStickyNotesNodes = [...stickyNotesNodes];

    const sweepTextNodes = [...textNodes];

    return {
      sweepGroups,
      sweepEdges,
      sweepNodes,
      sweepStandaloneNodes,
      sweepSections,
      sweepStickyNotesNodes,
      sweepTextNodes,
    };
  }, [
    sfFunnelNodes.nodes,
    sfFunnelNodes.edges,
    sfFunnelNodes.groups,
    thirdPartyNodes.nodes,
    thirdPartyNodes.edges,
    thirdPartyNodes.groups,
    metadataElementStandaloneNodes.standaloneNodes,
    metadataElementStandaloneNodes.edges,
    sectionNodes.nodes,
    stickyNotesNodes,
    textNodes,
  ]);
};
